import { AlertTriangle, Check, FileSearch, RefreshCw, X } from "lucide-react";
import * as React from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  useDecidirLote,
  useExecucoesColeta,
  useExtrairAto,
  useIndicesFiscais,
  useLotesPendentes,
} from "@/hooks/queries";
import type { ExtracaoAto, LotePropostas, ResultadoVarredura, TipoAto } from "@/types";

function dataCurta(iso: string | null | undefined) {
  if (!iso) return "—";
  // A API devolve UTC sem sufixo de fuso; sem o "Z" o navegador leria como horário local.
  const utc = /(Z|[+-]\d\d:?\d\d)$/.test(iso) ? iso : `${iso}Z`;
  return new Date(utc).toLocaleString("pt-BR", {
    dateStyle: "short",
    timeStyle: "short",
    timeZone: "America/Sao_Paulo",
  });
}

function vigenciaCurta(par: [string | null, string | null] | null) {
  if (!par) return "—";
  const [inicio, fim] = par;
  const fmt = (s: string | null) => (s ? new Date(s).toLocaleDateString("pt-BR") : "");
  if (inicio && fim) return `${fmt(inicio)} a ${fmt(fim)}`;
  if (inicio) return `desde ${fmt(inicio)}`;
  return "sem vigência";
}

/** "2026-05-01" → "01/05/2026" sem passar por Date (evita o deslocamento de fuso). */
function dataIso(iso: string | null | undefined) {
  if (!iso) return "—";
  const [a, m, d] = iso.split("-");
  return `${d}/${m}/${a}`;
}

const ROTULO_ACAO: Record<ResultadoVarredura["acao"], string> = {
  proposta: "na fila",
  inalterado: "inalterado(s)",
  ausente: "fora do ato",
  anterior: "ato anterior",
  erro: "erro",
};

/** "3 na fila · 20 inalterado · 1 erro" — o histórico não lista 27 índices um a um. */
function resumoAcoes(resultado: ResultadoVarredura[]) {
  const contagem = new Map<ResultadoVarredura["acao"], number>();
  for (const r of resultado) contagem.set(r.acao, (contagem.get(r.acao) ?? 0) + 1);
  return [...contagem].map(([acao, n]) => `${n} ${ROTULO_ACAO[acao]}`).join(" · ");
}

/** Tabela dos índices declarados na base de regras. */
function TabelaIndices() {
  const { data, isLoading } = useIndicesFiscais();

  if (isLoading || !data) return <Skeleton className="h-72 w-full" />;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Índices monitorados</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {data.alertas.length > 0 && (
          <div className="rounded-lg border border-amber-300/60 bg-amber-50 p-3 text-xs leading-relaxed text-amber-900">
            <p className="mb-1 flex items-center gap-1.5 font-bold">
              <AlertTriangle className="size-3.5" /> Atenção
            </p>
            <ul className="list-inside list-disc space-y-0.5">
              {data.alertas.map((a) => (
                <li key={a}>{a}</li>
              ))}
            </ul>
          </div>
        )}
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Índice</TableHead>
              <TableHead>UF</TableHead>
              <TableHead>Vigente</TableHead>
              <TableHead>Vigência</TableHead>
              <TableHead>Próximo</TableHead>
              <TableHead>Fonte</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.indices.map((ind) => (
              <TableRow key={ind.parametro}>
                <TableCell>
                  <span className="block font-semibold">{ind.parametro}</span>
                  <span className="block text-[11px] text-muted-foreground">{ind.descricao}</span>
                </TableCell>
                <TableCell>{ind.uf || "—"}</TableCell>
                <TableCell className="font-bold tabular-nums">{ind.valorVigente ?? "—"}</TableCell>
                <TableCell className="text-xs">{vigenciaCurta(ind.vigenciaAtual)}</TableCell>
                <TableCell className="tabular-nums">
                  {ind.valorProximo ? (
                    <span className="text-xs">
                      <b>{ind.valorProximo}</b> desde{" "}
                      {ind.vigenciaProxima?.[0]
                        ? new Date(ind.vigenciaProxima[0]).toLocaleDateString("pt-BR")
                        : "—"}
                    </span>
                  ) : (
                    "—"
                  )}
                </TableCell>
                <TableCell className="whitespace-nowrap text-xs" title={ind.fonte}>
                  {ind.fonteVigente ?? "—"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

/** Um lote da fila: todas as propostas de um ato, aplicadas ou rejeitadas juntas. */
function CartaoLote({ lote, vigenteDe }: { lote: LotePropostas; vigenteDe: (p: string) => string }) {
  const decidir = useDecidirLote();
  const [motivo, setMotivo] = React.useState("");
  const { execucao, propostas } = lote;
  const execucaoId = execucao?.id ?? propostas[0]?.execucaoId;

  return (
    <Card>
      <CardContent className="space-y-3 pt-5">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Índice</TableHead>
              <TableHead className="text-right">Atual</TableHead>
              <TableHead className="text-right">Novo</TableHead>
              <TableHead>Desde</TableHead>
              <TableHead>Trecho do ato</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {propostas.map((p) => (
              <TableRow key={p.id}>
                <TableCell className="font-semibold">{p.parametro}</TableCell>
                <TableCell className="text-right tabular-nums text-muted-foreground">
                  {vigenteDe(p.parametro)}
                </TableCell>
                <TableCell className="text-right font-bold tabular-nums">{p.valor}</TableCell>
                <TableCell className="text-xs">{dataIso(p.vigencia[0])}</TableCell>
                <TableCell className="text-[11px]">
                  <details>
                    <summary className="cursor-pointer text-muted-foreground">ver</summary>
                    <pre className="mt-1 max-w-[36rem] whitespace-pre-wrap rounded-md bg-muted/60 p-2 leading-relaxed">
                      {p.trecho || "(trecho não disponível)"}
                    </pre>
                  </details>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            disabled={decidir.isPending || !execucaoId}
            onClick={() => execucaoId && decidir.mutate({ execucaoId, acao: "aprovar" })}
          >
            {decidir.isPending ? <RefreshCw className="animate-spin" /> : <Check />} Aplicar todos (
            {propostas.length})
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={decidir.isPending || !execucaoId}
            onClick={() => execucaoId && decidir.mutate({ execucaoId, acao: "rejeitar", motivo })}
          >
            <X /> Rejeitar lote
          </Button>
          <input
            className="h-8 min-w-0 flex-1 basis-48 rounded-md border border-input bg-transparent px-3 text-xs outline-none focus-visible:ring-2 focus-visible:ring-ring"
            placeholder="Motivo da rejeição (opcional)"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
          />
        </div>
        {decidir.error && <p className="text-xs text-red-600">{String(decidir.error)}</p>}
      </CardContent>
    </Card>
  );
}

/** Fila de aprovação: um lote por ato lido. */
function FilaPropostas() {
  const { data, isLoading } = useLotesPendentes();
  const { data: indices } = useIndicesFiscais();

  if (isLoading || !data) return <Skeleton className="h-48 w-full" />;

  const vigenteDe = (parametro: string) =>
    indices?.indices.find((i) => i.parametro === parametro)?.valorVigente ?? "—";

  if (!data.lotes.length) {
    return (
      <Card>
        <CardContent className="py-6 text-center text-sm text-muted-foreground">
          Nenhuma proposta pendente
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {data.lotes.map((lote) => (
        <CartaoLote
          key={lote.execucao?.id ?? lote.propostas[0].id}
          lote={lote}
          vigenteDe={vigenteDe}
        />
      ))}
    </div>
  );
}

/** Retorno curto da extração: a aprovação acontece no cartão do lote, na fila. */
function AvisoExtracao({ dados }: { dados: ExtracaoAto }) {
  if (dados.status === "falha" || !dados.tabela) {
    return <p className="text-xs text-red-600">Extração falhou: {dados.log || "sem detalhe"}</p>;
  }
  const erros = dados.resultado.filter((r) => r.acao === "erro");
  const novos = dados.resultado.filter((r) => r.acao === "proposta").length;
  return (
    <div className="space-y-1 text-xs">
      {novos === 0 && (
        <p className="text-muted-foreground">
          Nenhum valor novo — {resumoAcoes(dados.resultado) || "nenhum índice deste ato"}.
        </p>
      )}
      {erros.map((r) => (
        <p key={r.parametro} className="text-red-600">
          {r.parametro}: {r.detalhe}
        </p>
      ))}
    </div>
  );
}

/** Histórico das últimas extrações de ato. */
function HistoricoExecucoes() {
  const { data, isLoading } = useExecucoesColeta();
  if (isLoading || !data) return <Skeleton className="h-40 w-full" />;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Últimas extrações</CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Quando</TableHead>
              <TableHead>Ato</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Resultado</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.execucoes.map((e) => (
              <TableRow key={e.id}>
                <TableCell className="whitespace-nowrap text-xs">{dataCurta(e.iniciadaEm)}</TableCell>
                <TableCell className="max-w-[22rem] text-xs">
                  {e.escopo.startsWith("https://") ? (
                    <a className="underline" href={e.escopo} target="_blank" rel="noreferrer">
                      {e.status !== "falha" && e.log ? e.log : e.escopo}
                    </a>
                  ) : (
                    e.escopo || "—"
                  )}
                </TableCell>
                <TableCell className="text-xs font-bold">{e.status}</TableCell>
                <TableCell className="text-[11px] text-muted-foreground">
                  {e.status === "falha"
                    ? e.log.slice(0, 200)
                    : resumoAcoes(e.resultado ?? []) || "—"}
                </TableCell>
              </TableRow>
            ))}
            {!data.execucoes.length && (
              <TableRow>
                <TableCell colSpan={4} className="text-center text-sm text-muted-foreground">
                  Nenhuma extração ainda — cole a URL de um Ato COTEPE/PMPF acima.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

/** Um campo de URL por tipo de ato, cada um com o seu resultado logo abaixo. */
function ExtrairAto({
  tipo,
  titulo,
  ajuda,
  exemplo,
}: {
  tipo: TipoAto;
  titulo: string;
  ajuda: string;
  exemplo: string;
}) {
  const extrair = useExtrairAto();
  const [url, setUrl] = React.useState("");

  return (
    <section className="space-y-2">
      <div>
        <h3 className="text-sm font-bold">{titulo}</h3>
        <p className="text-[11px] text-muted-foreground">{ajuda}</p>
      </div>
      <form
        className="flex flex-wrap items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (url.trim()) extrair.mutate({ url: url.trim(), tipo });
        }}
      >
        <Input
          className="min-w-0 flex-1 basis-72 font-normal"
          type="url"
          placeholder={exemplo}
          value={url}
          onChange={(e) => setUrl(e.target.value)}
        />
        <Button type="submit" disabled={extrair.isPending || !url.trim()}>
          {extrair.isPending ? <RefreshCw className="animate-spin" /> : <FileSearch />}
          {extrair.isPending ? "Lendo o ato…" : "Extrair"}
        </Button>
      </form>
      {extrair.error && (
        <p className="text-xs text-red-600">Falha ao extrair: {String(extrair.error)}</p>
      )}
      {extrair.data && <AvisoExtracao dados={extrair.data} />}
    </section>
  );
}

/** Índices fiscais variáveis — sub-aba da Base TAX. */
export function IndicesFiscais() {
  return (
    <div className="space-y-5">
      <p className="text-sm leading-relaxed text-muted-foreground">
        Cole a URL do ato mais recente: a IA lê os valores, cada um é conferido no texto do ato e
        os que mudaram entram na fila de aprovação.
      </p>

      <Card>
        <CardContent className="space-y-5 pt-5">
          <ExtrairAto
            tipo="pmpf"
            titulo="PMPF — Ato COTEPE/PMPF"
            ajuda="Tabela de preço médio por UF (atualiza os pmpf_<uf>)."
            exemplo="https://www.confaz.fazenda.gov.br/legislacao/atos-pmpf/2026/pmpf025_26"
          />
          <ExtrairAto
            tipo="ad_rem"
            titulo="ICMS ad rem do EAC — Convênio ICMS"
            ajuda={
              "Convênio que dá nova redação à cláusula sétima do Conv. ICMS 15/2023" +
              " (atualiza o icms_eac_ad_rem)."
            }
            exemplo="https://www.confaz.fazenda.gov.br/legislacao/convenios/2025/CV112_25"
          />
        </CardContent>
      </Card>

      <FilaPropostas />
      <TabelaIndices />
      <HistoricoExecucoes />
    </div>
  );
}
